/**
 * Mega Menu
 * 
 * This script handles the mega menu navigation functionality.
 * Features:
 * - Multi-level navigation with smooth transitions
 * - Back button functionality
 * - Dynamic title updates
 * - Proper state management
 * - Communication with main nav to hide/show dropdown toggle on mobile
 * - Proper accessibility and focus management
 * 
 * @type {Drupal.Behavior}
 */
(function (Drupal, once) {
  'use strict';

  Drupal.behaviors.megaMenu = {
    attach: function (context, settings) {
      // Set up mega menu instances
      once('mega-menu', '.mega-menu', context).forEach(function (megaMenu) {        
        // Cache DOM elements
        const navLists = megaMenu.querySelectorAll('[data-nav-level]');
        const backBtn = megaMenu.querySelector('[data-back-btn]');
        const titleElement = megaMenu.querySelector('[data-current-title]');
        const categoryLinks = megaMenu.querySelectorAll('[data-nav-trigger]');
        
        // Find the related main nav dropdown toggle
        const dropdownElement = megaMenu.closest('.dropdown');
        const mainNavToggle = dropdownElement ? dropdownElement.querySelector('.dropdown-toggle') : null;
        
        // State management
        let currentLevel = 0;
        let currentParentPath = '';
        let titleStack = []; // Stack to track title history for back navigation
        let focusStack = []; // Stack to track focus for proper return focus
        
        // Initialize - store the original title
        const originalTitle = titleElement.textContent.trim();
        titleStack.push(originalTitle);
        
        // Mobile detection function
        function isMobile() {
          return window.innerWidth <= 992;
        }
        
        /**
         * Dispatch custom events to communicate with main nav
         */
        function dispatchMainNavEvent(eventType, data = {}) {
          if (mainNavToggle) {
            const event = new CustomEvent(`megaMenu.${eventType}`, {
              detail: {
                toggle: mainNavToggle,
                level: currentLevel,
                isMobile: isMobile(),
                ...data
              }
            });
            document.dispatchEvent(event);
          }
        }
        
        /**
         * Show the back button and make it focusable
         */
        function showBackButton() {
          // Remove d-none and make focusable
          backBtn.classList.remove('d-none');
          backBtn.removeAttribute('tabindex'); // Make focusable again
          backBtn.setAttribute('aria-hidden', 'false');
          // console.log('Back button shown and made focusable');
        }

        /**
         * Hide the back button and remove from tab order
         */
        function hideBackButton() {
          // Add d-none and make unfocusable
          backBtn.classList.add('d-none');
          backBtn.setAttribute('tabindex', '-1'); // Remove from tab order
          backBtn.setAttribute('aria-hidden', 'true');
          // console.log('Back button hidden and removed from tab order');
        }
        
        /**
         * Show a specific navigation level
         * @param {number} level - The level to show
         * @param {string} parentPath - The parent path for this level
         * @param {string} newTitle - The title to display
         * @param {Element} triggerElement - The element that triggered this navigation
         */
        function showLevel(level, parentPath, newTitle, triggerElement = null) {
          // Store the trigger element for focus restoration
          if (triggerElement) {
            focusStack.push(triggerElement);
            // Update aria-expanded on the trigger button
            triggerElement.setAttribute('aria-expanded', 'true');
          }

          // Hide all levels and mark as aria-hidden
          navLists.forEach(function(navList) {
            navList.classList.add('d-none');
            navList.setAttribute('aria-hidden', 'true');
            // Remove focusable elements from tab order when hidden
            navList.querySelectorAll('a, button').forEach(function(el) {
              el.setAttribute('tabindex', '-1');
            });
          });

          // Show the target level
          const targetLevel = megaMenu.querySelector(`[data-nav-level="${level}"][data-parent="${parentPath}"]`);
          if (targetLevel) {
            targetLevel.classList.remove('d-none');
            targetLevel.setAttribute('aria-hidden', 'false');
            // Restore tab order for focusable elements
            targetLevel.querySelectorAll('a, button').forEach(function(el) {
              el.removeAttribute('tabindex');
            });
          }

          // Update state
          const previousLevel = currentLevel;
          currentLevel = level;
          currentParentPath = parentPath;

          // Update title
          titleElement.textContent = newTitle;
          titleStack.push(newTitle);

          // Show/hide back button with proper focus management
          if (level > 0) {
            showBackButton();
            backBtn.focus();
          } else {
            hideBackButton();
          }

          // Communicate with main nav about level change
          if (previousLevel === 0 && currentLevel > 0) {
            // Just entered sublevel - hide main nav toggle on mobile
            dispatchMainNavEvent('sublevelEntered');
          } else if (previousLevel > 0 && currentLevel === 0) {
            // Just returned to root - show main nav toggle on mobile
            dispatchMainNavEvent('rootReturned');
          }

          console.log(`Navigated to level ${level}, parent: ${parentPath}, title: ${newTitle}`);
        }
        
        /**
         * Navigate back to the previous level
         */
        function navigateBack() {
          if (currentLevel <= 0) return;

          // Remove current title from stack
          titleStack.pop();

          // Calculate previous level and parent path
          const previousLevel = currentLevel - 1;
          let previousParentPath = '';

          if (previousLevel > 0) {
            // Extract parent path for previous level
            // Current path: "5-0" -> Previous path: "5"
            // Current path: "5" -> Previous path: ""
            const pathParts = currentParentPath.split('-');
            pathParts.pop(); // Remove last part
            previousParentPath = pathParts.join('-');
          }

          // Get previous title from stack
          const previousTitle = titleStack[titleStack.length - 1] || originalTitle;

          // Hide all levels and update aria-hidden
          navLists.forEach(function(navList) {
            navList.classList.add('d-none');
            navList.setAttribute('aria-hidden', 'true');
            // Remove focusable elements from tab order when hidden
            navList.querySelectorAll('a, button').forEach(function(el) {
              el.setAttribute('tabindex', '-1');
            });
          });

          // Show the previous level
          let targetLevel = null;
          if (previousLevel === 0) {
            // Show root level
            targetLevel = megaMenu.querySelector('[data-nav-level="0"]');
          } else {
            // Show specific parent level
            targetLevel = megaMenu.querySelector(`[data-nav-level="${previousLevel}"][data-parent="${previousParentPath}"]`);
          }

          if (targetLevel) {
            targetLevel.classList.remove('d-none');
            targetLevel.setAttribute('aria-hidden', 'false');
            // Restore tab order for focusable elements
            targetLevel.querySelectorAll('a, button').forEach(function(el) {
              el.removeAttribute('tabindex');
            });
          }

          // Update state
          const oldLevel = currentLevel;
          currentLevel = previousLevel;
          currentParentPath = previousParentPath;

          // Update title
          titleElement.textContent = previousTitle;

          // Handle back button visibility and focus restoration
          if (currentLevel === 0) {
            hideBackButton();

            // Restore focus to the original trigger element and reset aria-expanded
            const originalTrigger = focusStack.pop();
            if (originalTrigger && originalTrigger.closest('.mega-menu')) {
              originalTrigger.setAttribute('aria-expanded', 'false');
              setTimeout(function() {
                originalTrigger.focus();
                console.log('Focus restored to original trigger');
              }, 50);
            }
          } else {
            // Still in a sublevel, keep back button visible
            showBackButton();
            // Reset aria-expanded on the trigger we're backing out of
            const lastTrigger = focusStack[focusStack.length - 1];
            if (lastTrigger) {
              lastTrigger.setAttribute('aria-expanded', 'false');
            }
          }

          // Communicate with main nav about level change
          if (oldLevel > 0 && currentLevel === 0) {
            // Just returned to root - show main nav toggle on mobile
            dispatchMainNavEvent('rootReturned');
          }

          console.log(`Navigated back to level ${currentLevel}, parent: ${currentParentPath}, title: ${previousTitle}`);
        }
        
        /**
         * Reset to root level (useful when mega menu is opened/closed)
         */
        function resetToRoot() {
          // Hide all levels except root and manage aria-hidden
          navLists.forEach(function(navList) {
            const level = parseInt(navList.getAttribute('data-nav-level'));
            if (level === 0) {
              navList.classList.remove('d-none');
              navList.setAttribute('aria-hidden', 'false');
              // Restore tab order for focusable elements
              navList.querySelectorAll('a, button').forEach(function(el) {
                el.removeAttribute('tabindex');
              });
            } else {
              navList.classList.add('d-none');
              navList.setAttribute('aria-hidden', 'true');
              // Remove from tab order when hidden
              navList.querySelectorAll('a, button').forEach(function(el) {
                el.setAttribute('tabindex', '-1');
              });
            }
          });

          // Reset aria-expanded on all category links
          categoryLinks.forEach(function(link) {
            link.setAttribute('aria-expanded', 'false');
          });

          // Update state
          const wasInSublevel = currentLevel > 0;
          currentLevel = 0;
          currentParentPath = '';
          titleStack = [originalTitle];
          focusStack = []; // Clear focus stack

          // Reset title and hide back button
          titleElement.textContent = originalTitle;
          hideBackButton();

          // Communicate with main nav about reset
          if (wasInSublevel) {
            dispatchMainNavEvent('rootReturned');
          }

          console.log('Reset to root level');
        }
        
        // Event Listeners
        
        // Handle category link clicks (items with children)
        categoryLinks.forEach(function(link) {
          link.addEventListener('click', function(e) {
            e.preventDefault();
            
            const trigger = this.getAttribute('data-nav-trigger');
            
            // Get the link text from the associated nav link, not the button
            const navItem = this.closest('.mega-menu__nav-item');
            const navLink = navItem.querySelector('.mega-menu__nav-link');
            const linkText = navLink ? navLink.textContent.trim() : this.textContent.trim();
            
            // Parse trigger: "1-5-0" -> level=1, parent="5", index=0
            const triggerParts = trigger.split('-');
            const nextLevel = parseInt(triggerParts[0]) + 1;
            const nextParentPath = triggerParts.slice(1).join('-');
            
            console.log(`Category clicked: ${linkText}, trigger: ${trigger}, next level: ${nextLevel}, parent path: ${nextParentPath}`);
            
            // Show the next level with focus management
            showLevel(nextLevel, nextParentPath, linkText, this);
          });
          
          // Add keyboard support for arrow buttons
          link.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.click();
            }
          });
        });
        
        // Handle back button clicks
        if (backBtn) {
          backBtn.addEventListener('click', function(e) {
            e.preventDefault();
            navigateBack();
          });

          // Add keyboard support for back button
          backBtn.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigateBack();
            }
          });
        }

        // Add global keyboard support for the mega menu
        megaMenu.addEventListener('keydown', function(e) {
          // Escape key - go back or close menu
          if (e.key === 'Escape') {
            e.preventDefault();
            if (currentLevel > 0) {
              navigateBack();
            } else if (dropdownElement) {
              // Close the dropdown
              const bsDropdown = bootstrap.Dropdown.getInstance(dropdownElement.querySelector('.dropdown-toggle'));
              if (bsDropdown) {
                bsDropdown.hide();
              }
            }
            return;
          }

          // Arrow key navigation within visible menu
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const visibleList = megaMenu.querySelector('[data-nav-level]:not(.d-none)');
            if (!visibleList) return;

            const focusableItems = Array.from(visibleList.querySelectorAll('a:not([tabindex="-1"]), button:not([tabindex="-1"])'));
            if (focusableItems.length === 0) return;

            const currentIndex = focusableItems.indexOf(document.activeElement);
            let nextIndex;

            if (e.key === 'ArrowDown') {
              e.preventDefault();
              nextIndex = currentIndex < focusableItems.length - 1 ? currentIndex + 1 : 0;
            } else {
              e.preventDefault();
              nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableItems.length - 1;
            }

            focusableItems[nextIndex].focus();
          }

          // Home/End key navigation
          if (e.key === 'Home' || e.key === 'End') {
            const visibleList = megaMenu.querySelector('[data-nav-level]:not(.d-none)');
            if (!visibleList) return;

            const focusableItems = Array.from(visibleList.querySelectorAll('a:not([tabindex="-1"]), button:not([tabindex="-1"])'));
            if (focusableItems.length === 0) return;

            e.preventDefault();
            if (e.key === 'Home') {
              focusableItems[0].focus();
            } else {
              focusableItems[focusableItems.length - 1].focus();
            }
          }
        });
        
        // Reset to root when mega menu is shown (Bootstrap dropdown event)
        if (dropdownElement) {
          dropdownElement.addEventListener('shown.bs.dropdown', function() {
            resetToRoot();
          });
          
          // When dropdown is hidden, always restore main nav toggle
          dropdownElement.addEventListener('hidden.bs.dropdown', function() {
            dispatchMainNavEvent('megaMenuClosed');
          });
        }

        // Footer menu visibility control
        const footerMenuWrapper = document.querySelector('.mobile-footer-nav-wrapper');

        if (dropdownElement && footerMenuWrapper) {
          // Hide footer when this mega menu opens
          dropdownElement.addEventListener('shown.bs.dropdown', function() {
            if (isMobile()) {
              footerMenuWrapper.classList.add('d-none');
              // console.log('Footer menu hidden - mega menu opened');

              // Hide sibling menu items
              const navList = dropdownElement.parentElement; // The <ul> containing all nav items
              const allNavItems = navList.querySelectorAll('.nav-item');
              
              allNavItems.forEach(function(navItem) {
                // Hide all nav items except the current one
                if (navItem !== dropdownElement) {
                  // Hiding sibling nav item
                  navItem.classList.add('d-none');
                  
                }
              });
            }
          });
          
          // Show footer when this mega menu closes
          dropdownElement.addEventListener('hidden.bs.dropdown', function() {
            if (isMobile()) {
              footerMenuWrapper.classList.remove('d-none');
              // console.log('Footer menu shown - mega menu closed');

              // Show all sibling menu items again
              const navList = dropdownElement.parentElement;
              const allNavItems = navList.querySelectorAll('.nav-item');
              
              allNavItems.forEach(function(navItem) {
                // Showing sibling nav item
                navItem.classList.remove('d-none');
              });
            }
          });
        }
        
        // Initialize - ensure we start at root level
        resetToRoot();
      });

    }
  };
})(Drupal, once);